import React, { FC, ReactElement, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import DropKartLogo from "../../img/Dropkart22.png";
import { BASE_URL } from "../../constants/urlConstants";
import {
  HomeOutlined,
  ShoppingCartOutlined,
  LoginOutlined,
  LogoutOutlined,
  SearchOutlined,
  UserOutlined,
  MenuOutlined,
  AppstoreOutlined,
  ProfileOutlined,
} from "@ant-design/icons";
import { Link, useHistory, useLocation } from "react-router-dom";
import { Affix, Badge, Input, Button, Drawer, Divider, message } from "antd";
import axios from "axios";

import { selectUserFromUserState } from "../../redux-toolkit/user/user-selector";
import { selectCartItemsCount } from "../../redux-toolkit/cart/cart-selector";
import { MENU } from "../../constants/routeConstants";
import "./NavBar.scss";

const NavBar: FC = (): ReactElement => {
  const user = useSelector(selectUserFromUserState);
  const cartItemsCount = useSelector(selectCartItemsCount);
  const history = useHistory();
  const location = useLocation();

  const [text, setText] = useState("");
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth <= 992);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 992);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const handleSearch = async () => {
    const query = text.trim();
    if (!query) return;

    setLoading(true);
    try {
      const res = await axios.post(`${BASE_URL}/api/v1/perfumes/search/global/${query}`);
      const perfumes = res.data;
      if (!perfumes?.length) {
        message.info("No products found.");
        return;
      }
      history.push({
        pathname: MENU,
        state: { id: "all", searchText: query, perfumes },
      });
    } catch (error) {
      message.error("Failed to fetch search results.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    window.location.reload();
  };

  const go = (path: string) => {
    setDrawerVisible(false);
    history.push(path);
  };

  const activeBottom =
    location.pathname.startsWith("/cart")
      ? "cart"
      : location.pathname.startsWith("/account")
      ? "account"
      : location.pathname.startsWith("/menu")
      ? "products"
      : "home";


  return (
    <>
      <Affix offsetTop={0}>
        <div className="navbar">
          <div className="navbar-container">
            {/* LEFT: Hamburger (always), Logo (desktop only) */}
            <div className="navbar-left">
              <Button
                type="text"
                icon={<MenuOutlined />}
                className="navbar-toggle"
                onClick={() => setDrawerVisible(true)}
              />
              <Link to="/" className="navbar-logo desktop-only">
                <img src={DropKartLogo} alt="DropKart Logo" className="navbar-logo-img" />
              </Link>
            </div>

            {/* CENTER: Search (mobile takes full width) */}
            <div className="navbar-search">
              <Input
                prefix={<SearchOutlined style={{ color: "#9aa0a6" }} />}
                placeholder="Search for products, brands and more"
                value={text}
                onChange={(e) => setText(e.target.value)}
                onPressEnter={handleSearch}
                allowClear
                disabled={loading}
              />
              {/* Desktop button only */}
              {!isMobile && (
                <Button type="primary" onClick={handleSearch} loading={loading}>
                  Search
                </Button>
              )}
            </div>

            {/* RIGHT: Links (desktop only) */}
            <div className="navbar-links desktop-only">
              <Link to="/">
                <HomeOutlined /> Home
              </Link>
              <Link to={{ pathname: MENU, state: { id: "all" } }}>
                <AppstoreOutlined /> Products
              </Link>
              <Link to="/cart" className="navbar-cart">
                <Badge count={cartItemsCount}>
                  <ShoppingCartOutlined /> Cart
                </Badge>
              </Link>
              {user ? (
                <>
                  <Link to="/account/user/info">
                    <UserOutlined /> Profile
                  </Link>
                  <Button
                    icon={<LogoutOutlined />}
                    type="link"
                    onClick={handleLogout}
                    style={{ color: "white" }}
                  >
                    Logout
                  </Button>
                </>
              ) : (
                <Link to="/login">
                  <LoginOutlined /> Login
                </Link>
              )}
            </div>
          </div>

          {/* Mobile-only: we purposely show NO logo / cart / login in the header */}
        </div>
      </Affix>

      {/* Flipkart-style Drawer (mobile) */}
      <Drawer
        title={
          <div className="drawer-header">
            <img src={DropKartLogo} alt="DropKart" className="drawer-logo" />
          </div>
        }
        placement="left"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        width="78%"
        className="navbar-drawer"
        bodyStyle={{ padding: 0, background: "#fff" }}
      >
        <div className="drawer-content">
          <button className="drawer-item" onClick={() => go("/")}>
            <HomeOutlined /> Home
          </button>
          <Divider />
          <button
            className="drawer-item"
            onClick={() => go(MENU)}
          >
            <AppstoreOutlined /> Products
          </button>
          <Divider />
          <button className="drawer-item" onClick={() => go("/cart")}>
            <Badge count={cartItemsCount} offset={[8, -2]}>
              <ShoppingCartOutlined />
            </Badge>
            My Cart
          </button>
          <Divider />
          {user ? (
            <>
              <button className="drawer-item" onClick={() => go("/account/user/info")}>
                <ProfileOutlined /> My Account
              </button>
              <Divider />
              <button className="drawer-item logout-btn" onClick={handleLogout}>
                <LogoutOutlined /> Logout
              </button>
            </>
          ) : (
            <button className="drawer-item" onClick={() => go("/login")}>
              <LoginOutlined /> Login
            </button>
          )}
        </div>
      </Drawer>

      {/* ✅ Mobile bottom navigation (Home | Products | Account | Cart) */}
      {isMobile && (
        <nav className="mobile-bottom-nav">
          <button
            className={`mbn-item ${activeBottom === "home" ? "active" : ""}`}
            onClick={() => go("/")}
          >
            <HomeOutlined className="mbn-icon" />
            <span>Home</span>
          </button>

          <button
            className={`mbn-item ${activeBottom === "products" ? "active" : ""}`}
            onClick={() => go("/menu")}
          >
            <AppstoreOutlined className="mbn-icon" />
            <span>Products</span>
          </button>

          <button
            className={`mbn-item ${activeBottom === "account" ? "active" : ""}`}
            onClick={() => go(user ? "/account/user/info" : "/login")}
          >
            <UserOutlined className="mbn-icon" />
            <span>Account</span>
          </button>

          <button
            className={`mbn-item ${activeBottom === "cart" ? "active" : ""}`}
            onClick={() => go("/cart")}
          >
            <Badge count={cartItemsCount} offset={[0, 2]}>
              <ShoppingCartOutlined className="mbn-icon" />
            </Badge>
            <span>Cart</span>
          </button>
        </nav>
      )}
    </>
  );
};

export default NavBar;
